import { useEffect, useState } from 'react';
import { ActivityIndicator, Alert, Pressable, ScrollView, Text, View } from 'react-native';
import type { Box } from '../store/types';
import { newTask, saveTask } from '../store/planner';
import { archiveBox } from '../store/anotacoes';
import { organizarCaixa, type TarefaSugerida } from '../lib/iaUsos';
import { fusoAparelho } from '../lib/fuso';
import { brYmd, horaTela, today } from '../lib/date';
import { durLabel } from '../lib/time';
import { Btn, Sheet } from './ui';
import { C, alpha } from './theme';
import { t } from '../lib/i18n';

/** A IA do usuário lê a caixa de anotações e sugere tarefas; a pessoa marca quais quer criar. */
export function OrganizarIA({ box, cor, onClose }: { box: Box | null; cor: string; onClose: () => void }) {
  const [lista, setLista] = useState<TarefaSugerida[] | null>(null);
  const [marcadas, setMarcadas] = useState<Set<number>>(new Set());
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!box) return;
    setLista(null); setErro('');
    organizarCaixa(box).then(l => { setLista(l); setMarcadas(new Set(l.map((_, i) => i))); })
      .catch(e => setErro(String(e?.message ?? e)));
  }, [box?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function criar() {
    if (!lista || !box) return;
    const escolhidas = lista.filter((_, i) => marcadas.has(i));
    for (const s of escolhidas) saveTask(newTask({
      title: s.titulo, start: s.data ?? today(), time: s.hora, duration: s.duracao, energy: s.energia, notes: s.notas,
      tz: s.hora ? fusoAparelho() : null, link: { aba: 'anotacoes', id: box.id, label: box.title || t('Anotação') },
    }));
    onClose();
    Alert.alert(t('{n} tarefa(s) criada(s)', { n: escolhidas.length }), t('Arquivar a caixa de anotações?'), [
      { text: t('Manter'), style: 'cancel' }, { text: t('Arquivar'), onPress: () => archiveBox(box.id) }]);
  }

  return (
    <Sheet visible={!!box} onClose={onClose} title={t('Organizar com IA')}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 60 }}>
        {!lista && !erro && <View style={{ alignItems: 'center', gap: 10, marginTop: 30 }}><ActivityIndicator color={cor} /><Text style={{ color: C.sub }}>{t('A IA está lendo a caixa...')}</Text></View>}
        {!!erro && <Text style={{ color: C.warn }}>{erro}</Text>}
        {lista && !lista.length && <Text style={{ color: C.sub }}>{t('A IA não encontrou tarefas nesta caixa.')}</Text>}
        {lista?.map((s, i) => {
          const on = marcadas.has(i);
          return (
            <Pressable key={i} onPress={() => setMarcadas(m => { const n = new Set(m); if (n.has(i)) n.delete(i); else n.add(i); return n; })}
              style={{ flexDirection: 'row', gap: 12, padding: 12, borderRadius: 12, backgroundColor: on ? alpha(cor, 0.14) : C.card }}>
              <Text style={{ fontSize: 18, color: on ? cor : C.mute }}>{on ? '☑' : '☐'}</Text>
              <View style={{ flex: 1, gap: 2 }}>
                <Text style={{ color: C.text, fontWeight: '700' }}>{s.titulo}</Text>
                <Text style={{ color: C.sub, fontSize: 12 }}>{[s.data ? brYmd(s.data) : t('Hoje'), s.hora ? horaTela(s.hora) : t('Sem horário'), durLabel(s.duracao), `⚡${s.energia}`].join(' · ')}</Text>
                {!!s.notas && <Text style={{ color: C.mute, fontSize: 12 }}>{s.notas}</Text>}
              </View>
            </Pressable>
          );
        })}
        {!!lista?.length && <Btn cor={cor} disabled={!marcadas.size} label={t('Criar {n} tarefa(s)', { n: marcadas.size })} onPress={criar} />}
      </ScrollView>
    </Sheet>
  );
}
